'use client'

import React, { useState } from 'react'
import { FilePdfIcon } from '@phosphor-icons/react/dist/ssr/FilePdf'
import { FilePlusIcon } from '@phosphor-icons/react/dist/ssr/FilePlus'
import { cx } from 'class-variance-authority'

import type { ToastType } from '@skop-app/lib-domain/types'
import { formatFileSize } from '@skop-app/lib-domain/utils'

type FileUploadProps = {
  toast: ToastType
  file: any
  setFile: (file: any) => void
  setData: (file: any) => void
  deleteCurrent: () => void
  accept: {
    type: string
    size: number
    sizeMessage: string
    defaultMessage: string
    message: string
  }
  disabled?: boolean
  className?: string
}

export const FileUpload = (props: FileUploadProps) => {
  const {
    toast,
    file,
    setFile,
    setData,
    deleteCurrent,
    accept,
    disabled,
    className,
  } = props

  const [dropActive, setDropActive] = useState(false)

  const handleFile = (e) => {
    let file: File | null = null
    if ('dataTransfer' in e && e.dataTransfer?.files?.length > 0) {
      file = e.dataTransfer?.files?.[0]
    } else if (
      'target' in e &&
      e.target instanceof HTMLInputElement &&
      e.target.files?.length > 0
    ) {
      file = e.target?.files?.[0]
    }
    if (!file) return
    if (accept.type.indexOf(file?.type) === -1) {
      toast.error(`Le format du fichier n'est pas supporté. ${accept.message}`)
      return
    }
    if (file?.size > accept?.size) {
      toast.error(
        `Le fichier est trop volumineux, veuillez vérifier qu'il ne contient pas d'images. ${accept?.sizeMessage}`,
      )
      return
    }
    setFile(file)
    setData(file)
  }

  const dragOver = (e) => {
    e.preventDefault()
    setDropActive(true)
  }

  const dragleave = (e) => {
    e.preventDefault()
    setDropActive(false)
  }

  const drop = (e) => {
    e.preventDefault()
    if (disabled) return
    setDropActive(false)
    handleFile(e)
  }

  const onChange = (e: React.ChangeEvent<HTMLInputElement>) => handleFile(e)

  const onClick = (e: React.MouseEvent<HTMLInputElement>) => {
    const target = e.target as HTMLButtonElement
    if (target) target.value = ''
  }

  const fileInfo = `${new Date(file?.lastModified)?.toLocaleDateString(
    'fr-FR',
    {
      year: 'numeric',
      month: 'long',
      day: 'numeric',
    },
  )} | ${formatFileSize(file?.size)}`

  return (
    <div
      aria-disabled={disabled}
      className={cx(
        'flex justify-center',
        dropActive ? 'border-primary-500' : 'border-gray-300',
        !file && 'rounded-md border-2 border-dashed',
        className,
      )}
    >
      {file ? (
        <div className='p-8 text-center space-y-2'>
          <div className='flex items-center space-x-4 rounded-md border border-gray-200 py-2 px-4'>
            <FilePdfIcon className='mx-auto size-12 text-slate-300' />
            <div className='flex flex-col items-start'>
              <p>{file?.name}</p>
              {file?.size && (
                <p className='text-xs text-slate-300'>{fileInfo}</p>
              )}
            </div>
          </div>

          <div className='flex justify-center text-sm text-slate-300 space-x-2'>
            <label
              htmlFor='file'
              className='relative cursor-pointer rounded-md bg-white font-medium text-primary-500 focus-within:outline-none focus-within:ring-2 focus-within:ring-primary-500 focus-within:ring-offset-2 hover:text-primary-500/80'
            >
              Changer
            </label>
            <p>ou</p>
            <button
              type='button'
              className='font-medium text-primary-500 hover:text-primary-500/80 focus:underline focus:outline-none'
              onClick={deleteCurrent}
            >
              Annuler
            </button>
          </div>
        </div>
      ) : (
        <div
          className='w-full p-8 text-center'
          onDragOver={dragOver}
          onDragLeave={dragleave}
          onDrop={drop}
        >
          <label htmlFor='file' className='block py-2'>
            <FilePlusIcon
              className='mx-auto size-12 text-slate-300'
              aria-hidden='true'
            />
          </label>
          <div>
            <label
              aria-disabled={disabled}
              htmlFor='file'
              className='cursor-pointer space-x-2 aria-disabled:opacity-50 aria-disabled:cursor-not-allowed'
            >
              <span className='text-primary-500'>Sélectionnez un fichier</span>
              <span className='text-slate-300'>ou glissez-le ici</span>
            </label>
          </div>
          <p className='text-sm text-gray-500'>{accept.defaultMessage}</p>
        </div>
      )}
      <input
        id='file'
        name='file'
        type='file'
        className='sr-only'
        disabled={disabled}
        accept={accept.type}
        onChange={onChange}
        onClick={onClick}
      />
    </div>
  )
}
